import React, { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import ConversationListItem from './ConversationListItem';
import NewConversationModal from './NewConversationModal';

export default function ConversationList({
    conversations,
    activeConversationId,
    onSelectConversation,
    onConversationsUpdated
}) {
    const { theme } = useTheme();
    const [searchQuery, setSearchQuery] = useState('');
    const [showNewModal, setShowNewModal] = useState(false);

    const filteredConversations = conversations.filter(c => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
            c.display_name?.toLowerCase().includes(q) ||
            c.title?.toLowerCase().includes(q) ||
            c.last_message?.content?.toLowerCase().includes(q)
        );
    });

    const handleConversationCreated = (newConversation) => {
        onConversationsUpdated(prev => {
            const exists = prev.find(c => c.id === newConversation.id);
            if (exists) return prev;
            return [newConversation, ...prev];
        });
        onSelectConversation(newConversation);
    };

    return (
        <div className={`flex flex-col h-full border-r ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            {/* Header */}
            <div className={`flex items-center px-4 py-3 border-b ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <h2 className="text-xl font-dm-serif font-semibold">Messages</h2>
                <button
                    onClick={() => setShowNewModal(true)}
                    className="ml-2 p-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    title="New Conversation"
                >
                    <Plus size={14} />
                </button>
            </div>

            {/* Search */}
            <div className="px-3 py-2">
                <div className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                    theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                }`}>
                    <Search size={14} className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search conversations..."
                        className={`flex-1 bg-transparent border-none outline-none text-sm font-rasa ${
                            theme === 'dark' ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                        }`}
                    />
                </div>
            </div>

            {/* Conversations list */}
            <div className="flex-1 overflow-y-auto px-2 space-y-0.5">
                {filteredConversations.length === 0 ? (
                    <div className={`text-center py-8 text-sm font-rasa ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                        {searchQuery ? 'No matching conversations' : 'No conversations yet'}
                    </div>
                ) : (
                    (() => {
                        const pending = filteredConversations.filter(c => c.membership_status === 'pending');
                        const active  = filteredConversations.filter(c => c.membership_status !== 'pending');
                        return [...pending, ...active].map(conversation => (
                            <ConversationListItem
                                key={conversation.id}
                                conversation={conversation}
                                isActive={conversation.id === activeConversationId}
                                onClick={() => onSelectConversation(conversation)}
                                onConversationsUpdated={onConversationsUpdated}
                            />
                        ));
                    })()
                )}
            </div>

            {showNewModal && (
                <NewConversationModal
                    onClose={() => setShowNewModal(false)}
                    onConversationCreated={handleConversationCreated}
                />
            )}
        </div>
    );
}
